import moment from 'moment';

import {
  formatDateTooltip,
} from './tooltips';

import * as cellRenderers from './cellRenderers';
import * as cellStyles from './cellStyles';

const dateComparator = (valueA, valueB) => (
  moment(valueA) - moment(valueB)
);

const booleanFilterValueGetter = (params) => {
  if (params.data[params.colDef.field] === true) {
    return 'yes';
  }
  return 'no';
};

const locationValueGetter = (params) => {
  if (!params.data.latitude || !params.data.longitude) return undefined;
  return `${params.data.latitude}, ${params.data.longitude}`;
};

const dateColumnTemplate = {
  comparator: dateComparator,
  tooltipValueGetter: formatDateTooltip,
  cellRenderer: cellRenderers.dateCellRenderer,
  cellClass: 'status-cell',
};

const booleanColumnTemplate = {
  cellRenderer: cellRenderers.booleanCellRenderer,
  filterValueGetter: booleanFilterValueGetter,
};

const statsColumnTemplate = {
  tooltipValueGetter: () => 'View details',
  cellStyle: cellStyles.sensorStatsCellStyle,
  cellRenderer: cellRenderers.sensorStatsCellRenderer,
  cellClass: cellStyles.sensorStatsCellClass,
};

const smallColumnWidth = 55;
const actionColumnWidth = 96;
const mediumColumnWidth = 70;
const largeColumnWidth = 100;

const getFirstNumber = (data, keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const value = data[keys[i]];
    if (value !== undefined && value !== null && value !== '') {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue)) return numericValue;
    }
  }
  return null;
};

const getRanchTotalBlocks = (data) => getFirstNumber(data, [
  'blockCount',
  'blocksCount',
  'totalBlocks',
  'totalBlockCount',
]);

const ratioValueGetter = (data, numeratorKeys) => {
  const total = getRanchTotalBlocks(data);
  const numerator = getFirstNumber(data, numeratorKeys);
  if (total === null || numerator === null) return '-';
  return `${numerator}/${total}`;
};

export const columnDefs = {
  index: {
    headerName: '#',
    field: 'index',
    cellRenderer: (params) => params.rowIndex + 1,
    suppressMenu: true,
    sortable: false,
    pinned: 'left',
    width: 40,
  },
  id: {
    headerName: 'id',
  },
  ranchId: {
    headerName: 'Ranch ID',
    valueGetter: (params) => params.data?.id,
  },
  name: {
    pinned: 'left',
  },
  edit: {
    suppressMenu: true,
    sortable: false,
    pinned: 'right',
    cellRenderer: cellRenderers.editSensorCellRenderer,
    width: smallColumnWidth,
  },
  events: {
    sortable: false,
    cellRenderer: cellRenderers.eventSensorCellRender,
    width: largeColumnWidth,
  },
  editBlock: {
    suppressMenu: true,
    sortable: false,
    cellRenderer: cellRenderers.editBlockCellRenderer,
    width: smallColumnWidth,
  },
  editGateway: {
    headerName: 'Edit',
    suppressMenu: true,
    sortable: false,
    pinned: 'right',
    cellRenderer: cellRenderers.editSensorCellRendererGateway,
    width: smallColumnWidth,
  },
  identifier: {
    headerName: 'identifier (MAC)',
  },
  data_count: {
    headerName: 'Count',
  },
  // date columns
  data_latest_date: {
    ...dateColumnTemplate,
  },
  latest_date_posted: {
    ...dateColumnTemplate,
  },
  last_ping: {
    ...dateColumnTemplate,
    headerName: 'Last Online',
    cellStyle: cellStyles.pingCellStyle,
    cellRenderer: cellRenderers.lastOnlineCellRenderer,
  },
  weather_date: {
    ...dateColumnTemplate,
    cellStyle: cellStyles.dataDateCellStyle,
    cellRenderer: cellRenderers.gatewayWeatherDateCellRenderer,
  },
  epsolar_date: {
    ...dateColumnTemplate,
    headerName: 'Power Date',
    cellStyle: cellStyles.dataDateCellStyle,
    cellRenderer: cellRenderers.powerDateGraphCellRenderer,
  },
  heartbeat_date: {
    ...dateColumnTemplate,
    cellStyle: cellStyles.dataDateCellStyle,
  },
  date: {
    ...dateColumnTemplate,
  },
  last_boot_time: {
    ...dateColumnTemplate,
  },
  last_service_date: {
    ...dateColumnTemplate,
    cellRenderer: cellRenderers.lastServiceDateCellRenderer,
  },
  image: {
    cellRenderer: cellRenderers.imageCellRenderer,
  },
  // action columns
  create_note: {
    pinned: 'right',
    cellRenderer: cellRenderers.createNoteCellRenderer,
    width: smallColumnWidth,
    headerName: '+ Note',
    suppressMenu: true,
    sortable: false,
  },
  // cavalierActions: {
  //   headerName: 'Actions',
  //   pinned: 'right',
  //   cellRenderer: 'cavalierActionsMenu',
  //   width: smallColumnWidth,
  //   suppressMenu: true,
  //   sortable: false,
  //   cellStyle: {
  //     padding: '0px 5px',
  //   },
  // },
  sensorActions: {
    headerName: 'Actions',
    pinned: 'right',
    cellRenderer: 'sensorActionsMenu',
    width: actionColumnWidth,
    minWidth: actionColumnWidth,
    suppressMenu: true,
    sortable: false,
    cellStyle: {
      padding: '0px 5px',
    },
  },
  location: {
    cellRenderer: cellRenderers.locationCellRenderer,
    valueGetter: locationValueGetter,
  },
  gatewayLocation: {
    headerName: 'Location',
    cellRenderer: cellRenderers.gatewayLocationCellRenderer,
    valueGetter: locationValueGetter,
  },
  update_keys: {
    cellRenderer: cellRenderers.updateKeysCellRenderer,
  },
  // boolean columns
  visible: {
    ...booleanColumnTemplate,
    width: smallColumnWidth,
    headerName: 'Vis',
  },
  provisioned: {
    ...booleanColumnTemplate,
  },
  calibrated: {
    ...booleanColumnTemplate,
  },
  weather_online: {
    ...booleanColumnTemplate,
  },
  offline_for_2hrs: {
    ...booleanColumnTemplate,
    width: mediumColumnWidth,
    headerName: 'Off/2',
  },
  offline_for_24hrs: {
    ...booleanColumnTemplate,
    width: mediumColumnWidth,
    headerName: 'Off/24',
  },
  up: {
    ...booleanColumnTemplate,
    width: smallColumnWidth,
  },
  commissioned: {
    ...booleanColumnTemplate,
    width: smallColumnWidth,
    headerName: 'Com',
  },
  maintenance: {
    ...booleanColumnTemplate,
  },
  has_acpower: {
    ...booleanColumnTemplate,
    headerName: 'AC Power',
  },
  health_check: {
    ...booleanColumnTemplate,
  },
  internet_access: {
    ...booleanColumnTemplate,
  },
  isActive: {
    ...booleanColumnTemplate,
    headerName: 'Status',
  },
  recommendationsEnabled: {
    ...booleanColumnTemplate,
    headerName: 'Recommendations Enabled',
  },
  imageryEnabled: {
    ...booleanColumnTemplate,
    headerName: 'Imagery Enabled',
  },
  frostDetectionEnabled: {
    ...booleanColumnTemplate,
    headerName: 'Frost Detection Enabled',
  },
  // stats columns
  sensor_stats_cavalier: {
    ...statsColumnTemplate,
    headerName: 'Nodes',
  },
  sensor_stats_soil: {
    ...statsColumnTemplate,
    headerName: 'Soil Sensors',
  },
  sensor_stats_pressure: {
    ...statsColumnTemplate,
    headerName: 'Pressure Sensors',
  },
  sensor_stats_other: {
    ...statsColumnTemplate,
    headerName: 'Other Sensors',
  },
  // link columns
  errors: {
    filterValueGetter: (params) => {
      const errors = params.value;
      if (!errors) return 0;
      return errors.length;
    },
    cellRenderer: cellRenderers.errorsCellRenderer,
  },
  alarms: {
    cellRenderer: cellRenderers.alarmCountCellRender,
    width: largeColumnWidth,
  },
  anomaly_count_24hr: {
    headerName: 'Anom.24hr',
    cellRenderer: cellRenderers.dataAnomalyCount24hrCellRenderer,
    width: largeColumnWidth,
  },
  anomaly_count_2wk: {
    headerName: 'Anom.2wk',
    cellRenderer: cellRenderers.dataAnomalyCount2wkCellRenderer,
    width: largeColumnWidth,
  },
  sensor_anomalies_24hr: {
    headerName: 'Anom.24hr ',
    cellRenderer: cellRenderers.sensorAnomalyCount24hrCellRenderer,
    width: largeColumnWidth,
  },
  sensor_anomalies_2wk: {
    headerName: 'Anom.2wk',
    cellRenderer: cellRenderers.sensorAnomalyCount2wkCellRenderer,
    width: largeColumnWidth,
  },
  notes: {
    cellRenderer: cellRenderers.notesCellRenderer,
    width: mediumColumnWidth,
  },
  images: {
    cellRenderer: cellRenderers.imagesCellRenderer,
    width: mediumColumnWidth,
  },
  entity: {
    cellRenderer: cellRenderers.entityCellRenderer,
  },
  ranch: {
    // cellRenderer: cellRenderers.ranchCellRenderer,
  },
  block: {
    // cellRenderer: cellRenderers.blockCellRenderer,
  },
  gateway: {
    cellRenderer: cellRenderers.gatewayCellRenderer,
  },
  node: {
    cellRenderer: cellRenderers.nodeCellRenderer,
  },
  graph: {
    cellRenderer: cellRenderers.sensorGraphCellRenderer,
    width: mediumColumnWidth,
  },
  // sw versions
  cavalier_gateway_sw: {
    cellRenderer: cellRenderers.softwareVersionCellRenderer,
  },
  aquacheck_sw: {
    cellRenderer: cellRenderers.softwareVersionCellRenderer,
  },
  basestation_utilities_sw: {
    cellRenderer: cellRenderers.softwareVersionCellRenderer,
  },
  healthcheck_sw: {
    cellRenderer: cellRenderers.softwareVersionCellRenderer,
  },
  epsolar_sw: {
    cellRenderer: cellRenderers.softwareVersionCellRenderer,
  },
  weathergw_sw: {
    cellRenderer: cellRenderers.softwareVersionCellRenderer,
  },
  'dalmatian-web_sw': {
    cellRenderer: cellRenderers.softwareVersionCellRenderer,
  },
  // fw / hw revision
  fw_revision: {
    headerName: 'FW Rev',
  },
  hw_revision: {
    headerName: 'HW Rev',
  },
  // other status
  uptime: {
    cellRenderer: cellRenderers.uptimeCellRenderer,
  },
  ip_address: {
    cellRenderer: cellRenderers.ipAddressCellRenderer,
  },
  num_cavaliers: {
    cellRenderer: cellRenderers.numCavaliersCellRenderer,
  },
  num_sensors: {
    cellRenderer: cellRenderers.numSensorsCellRenderer,
  },
  num_provisioned: {
    cellRenderer: cellRenderers.numProvisionedCellRenderer,
    cellStyle: cellStyles.numProvisionedCellStyle,
    cellClass: 'status-cell',
  },
  validation_errors: {
    cellRenderer: cellRenderers.validationErrorsCellRenderer,
    cellStyle: cellStyles.statusCountCellStyle,
    cellClass: 'status-cell',
  },
  sensors: {
    cellRenderer: cellRenderers.cavalierSensorsCellRenderer,
    valueGetter: (params) => {
      if (!params.data.sensors) return 0;
      return params.data.sensors.length;
    },
  },
  cgsn: {
    headerName: 'IMEI',
  },
  entityId: {
    headerName: 'Entity ID',
  },
  entityName: {
    headerName: 'Entity Name',
    cellRenderer: cellRenderers.ranchEntityNameCopyCellRenderer,
  },
  installState: {
    headerName: 'Install State',
  },
  areaAcres: {
    headerName: 'Acres',
  },
  blockCount: {
    headerName: 'Block Count',
  },
  gatewayCount: {
    headerName: 'Gateway Count',
  },
  anomalyScore: {
    headerName: 'Anomaly Score',
  },
  gatewayConnected: {
    headerName: 'Gateway Count',
    valueGetter: (params) => params.data?.gateway_count ?? 0,
  },
  nodeConnected: {
    headerName: 'Node Connected',
  },
  sensorConnected: {
    headerName: 'Sensor Connected',
  },
  soilMoisture: {
    headerName: 'Soil Moisture',
  },
  pressure: {
    headerName: 'Pressure',
  },
  dataIssues: {
    headerName: 'Data Issues',
  },
  pendingAction: {
    headerName: 'Pending Action',
  },
  batteryStatus: {
    headerName: 'Battery Status',
  },
  chargeStatus: {
    headerName: 'Charge Status',
  },
  wsStatus: {
    headerName: 'WS Status',
  },
  distanceMi: {
    headerName: 'Dist (mi)',
  },
  address: {
    headerName: 'Address',
  },
  contact: {
    headerName: 'Contact',
  },
  recommendationsStatus: {
    headerName: 'Recommendations Status',
  },
  imageryStatusHealthy: {
    headerName: 'Imagery Status (Healthy)',
  },
  subscription: {
    headerName: 'Subscription',
  },
  regionCountry: {
    headerName: 'Region(country)',
  },
  recEnabledSummary: {
    headerName: 'Recommendations enabled',
    valueGetter: (params) => ratioValueGetter(params.data, [
      'recommendationsEnabledBlockCount',
      'recommendations_enabled_block_count',
      'recEnabledBlockCount',
      'rec_enabled_block_count',
    ]),
  },
  recStatusSummary: {
    headerName: 'Recommendations status',
    valueGetter: (params) => ratioValueGetter(params.data, [
      'recommendationsHealthyBlockCount',
      'recommendations_healthy_block_count',
      'recommendationsStatusHealthyBlockCount',
      'recHealthyBlockCount',
      'rec_healthy_block_count',
    ]),
  },
  imageryEnabledSummary: {
    headerName: 'Imagery enabled',
    valueGetter: (params) => ratioValueGetter(params.data, [
      'imageryEnabledBlockCount',
      'imagery_enabled_block_count',
      'imageryEnabledCount',
    ]),
  },
  imageryStatusSummary: {
    headerName: 'Imagery status',
    valueGetter: (params) => ratioValueGetter(params.data, [
      'imageryHealthyBlockCount',
      'imagery_healthy_block_count',
      'imageryStatusHealthyBlockCount',
      'imagery_status_healthy_block_count',
    ]),
  },
};

export const mobileColumnDefinitions = {
  ...columnDefs,
  name: {
    pinned: null,
  },
}
